import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Typography, Box, Grid,
  Avatar, Chip, Divider, CircularProgress, Alert,
  IconButton, Stack, Tooltip
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonIcon from '@mui/icons-material/Person';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import API_BASE_URL from '../config';

// Exact payload fields matching backend schema — same as EditProfile
const PAYLOAD_FIELDS = [
  'prefix', 'first_name', 'middle_name', 'last_name', 'display_name',
  'gender', 'dob', 'age', 'phone_number', 'email_id',
  'address', 'area', 'village_name', 'district', 'state', 'pincode', 'position', 'password',
];

const PREFIX_OPTIONS = ['Mr', 'Mrs', 'Miss', 'Dr',"Eng"];
const GENDER_OPTIONS = ['Male', 'Female', 'Other'];

// Styled components matching EditProfile styles
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    transition: 'all 0.3s ease',
    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.02) },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.primary.main, 0.03),
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
}));

const SectionCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
  boxShadow: `0 4px 20px 0 ${alpha(theme.palette.common.black, 0.05)}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: '3px',
    background: t => `linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
  },
}));

const InfoIcon = styled(Box)(({ theme }) => ({
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 36, height: 36, borderRadius: '50%',
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  marginRight: theme.spacing(1.5),
}));

const GradientButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  padding: theme.spacing(1, 3),
  textTransform: 'none',
  fontWeight: 600,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  color: 'white',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: `0 5px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  '&.Mui-disabled': { background: theme.palette.action.disabledBackground },
}));

// Calculate age from DOB — same logic as EditProfile
const calculateAge = (dob) => {
  if (!dob) return '';
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return String(age);
};

const MemberProfileDialog = ({ memberId, relationship, open, onClose, onMemberUpdated }) => {
  const [member, setMember] = useState(null);
  const [formData, setFormData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [copied, setCopied] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    if (!open || !memberId) return;
    setEditMode(false);
    setError(null);
    setSuccess(false);
    setFieldErrors({});
    fetchMember();
  }, [open, memberId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMember = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/user/${memberId}`, { withCredentials: true });
      const userData = res.data.user || res.data;
      setMember(userData);
      setFormData(userData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load member profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => { const e = { ...prev }; delete e[name]; return e; });
    }
  };

  // Same DOB handler as EditProfile
  const handleDOBChange = (value) => {
    const age = calculateAge(value);
    setFormData(prev => ({ ...prev, dob: value, age }));
  };

  // Pincode auto-fetch using India Post API
  const handlePincodeChange = async (value) => {
    handleChange('pincode', value);
    if (value.length !== 6 || !/^\d{6}$/.test(value)) return;
    setPincodeLoading(true);
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${value}`);
      const data = await res.json();
      if (data?.[0]?.Status === 'Success' && data[0].PostOffice?.length > 0) {
        const po = data[0].PostOffice[0];
        setFormData(prev => ({
          ...prev,
          pincode: value,
          state: po.State || prev.state,
          district: po.District || prev.district,
          village_name: po.Name || prev.village_name,
          area: po.Block || prev.area,
        }));
      }
    } catch (_) {
      // Silently fail — fields remain editable
    } finally {
      setPincodeLoading(false);
    }
  };

  const validate = () => {
    const errs = {};
    if (!String(formData.first_name || '').trim()) errs.first_name = 'Required';
    if (!String(formData.last_name || '').trim()) errs.last_name = 'Required';
    if (!String(formData.gender || '').trim()) errs.gender = 'Required';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!member?.uid) {
      setError('Invalid Member UID. Cannot update.');
      return;
    }
    if (!validate()) return;

    const payload = {};
    PAYLOAD_FIELDS.forEach(key => {
      payload[key] = formData[key] ?? '';
    });

    console.log('UID:', member.uid);
    console.log('Payload:', payload);

    setSaving(true);
    setError(null);
    try {
      const res = await axios.put(`${API_BASE_URL}/admin/users/${member.uid}`, payload, { withCredentials: true });

      const updated = res.data.user || { ...member, ...payload };
      setMember(updated);
      setFormData(updated);
      setEditMode(false);
      setSuccess(true);
      setSuccessOpen(true); // Open the success modal

      // Refresh the tree and close modal after a short delay
      setTimeout(() => {
        setSuccessOpen(false);
        if (onMemberUpdated) onMemberUpdated(updated);
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setFormData(member);
    setFieldErrors({});
    setError(null);
  };

  const handleClose = () => {
    setEditMode(false);
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handleCopyUid = () => {
    navigator.clipboard.writeText(member?.uid || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const isSelf = relationship === 'Self';
  const f = (key) => formData[key] || '';

  return (
    <>
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" scroll="paper">
      {/* Title */}
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: t => `linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
        color: 'white', px: 3, py: 2,
      }}>
        <Typography fontWeight="bold" fontSize="1.1rem">
          Member Profile{relationship ? ` — ${relationship}` : ''}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: 'white' }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!loading && success && <Alert severity="success" sx={{ mb: 2 }}>Profile updated successfully!</Alert>}

        {!loading && member && (
          <>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, mt: 1 }}>
              <Avatar sx={{
                width: 72, height: 72, fontSize: '1.8rem',
                background: t => `linear-gradient(45deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
              }}>
                {member.first_name?.[0]?.toUpperCase() || '?'}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {[member.prefix, member.first_name, member.middle_name, member.last_name].filter(Boolean).join(' ')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap">
                  <Tooltip title={copied ? 'Copied!' : 'Copy UID'}>
                    <Chip
                      label={`UID: ${member.uid || 'N/A'}`}
                      size="small"
                      variant="outlined"
                      icon={<ContentCopyIcon fontSize="small" />}
                      onClick={handleCopyUid}
                      sx={{ cursor: 'pointer', borderRadius: 1.5, fontWeight: 600 }}
                    />
                  </Tooltip>
                  {relationship && <Chip label={relationship} size="small" color="primary" />}
                </Stack>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* ── SECTION 1: Basic Info ── */}
            <SectionCard>
              <Stack direction="row" alignItems="center" mb={2}>
                <InfoIcon sx={{ color: editMode ? 'primary.main' : 'text.secondary', opacity: editMode ? 1 : 0.6 }}>
                  <PersonIcon fontSize="small" />
                </InfoIcon>
                <Typography variant="subtitle1" fontWeight="bold" sx={{
                  ...(editMode && {
                    background: t => `linear-gradient(45deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
                    backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent',
                  }),
                  ...(!editMode && { color: 'text.secondary' }),
                }}>
                  Basic Information
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {/* Prefix dropdown */}
                <Grid item xs={12} sm={6} md={3}>
                  <StyledTextField
                    select label="Prefix" value={f('prefix')}
                    onChange={e => handleChange('prefix', e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                  >
                    <MenuItem value="">Select</MenuItem>
                    {PREFIX_OPTIONS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </StyledTextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StyledTextField
                    label="First Name *" value={f('first_name')}
                    onChange={e => handleChange('first_name', e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                    error={!!fieldErrors.first_name} helperText={fieldErrors.first_name}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StyledTextField
                    label="Middle Name" value={f('middle_name')}
                    onChange={e => handleChange('middle_name', e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StyledTextField
                    label="Last Name *" value={f('last_name')}
                    onChange={e => handleChange('last_name', e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                    error={!!fieldErrors.last_name} helperText={fieldErrors.last_name}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <StyledTextField
                    label="Display Name" value={f('display_name')}
                    onChange={e => handleChange('display_name', e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <StyledTextField
                    select label="Gender *" value={f('gender')}
                    onChange={e => handleChange('gender', e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                    error={!!fieldErrors.gender} helperText={fieldErrors.gender}
                  >
                    <MenuItem value="">Select</MenuItem>
                    {GENDER_OPTIONS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                  </StyledTextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <StyledTextField
                    label="Position" value={f('position')}
                    onChange={e => handleChange('position', e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                  />
                </Grid>
              </Grid>
              {editMode && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <StyledTextField
                      label="Set Login Password" value={f('password')}
                      onChange={e => handleChange('password', e.target.value)}
                      fullWidth size="small" type="text" placeholder="Allow this member to login with this password"
                    />
                  </Grid>
                </Grid>
              )}
            </SectionCard>

            {/* ── SECTION 2: Contact Info ── */}
            <SectionCard>
              <Stack direction="row" alignItems="center" mb={2}>
                <InfoIcon sx={{ color: editMode ? 'primary.main' : 'text.secondary', opacity: editMode ? 1 : 0.6 }}>
                  <ContactPhoneIcon fontSize="small" />
                </InfoIcon>
                <Typography variant="subtitle1" fontWeight="bold" sx={{
                  ...(editMode && {
                    background: t => `linear-gradient(45deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
                    backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent',
                  }),
                  ...(!editMode && { color: 'text.secondary' }),
                }}>
                  Contact & Personal
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <StyledTextField
                    label="Phone Number" value={f('phone_number')}
                    onChange={e => handleChange('phone_number', e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                    inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StyledTextField
                    label="Email ID" value={f('email_id')}
                    onChange={e => handleChange('email_id', e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StyledTextField
                    label="Date of Birth" value={f('dob')}
                    onChange={e => handleDOBChange(e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                    type="date" InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  {/* Age is read-only, auto-calculated from DOB — same as EditProfile */}
                  <StyledTextField
                    label="Age" value={f('age')}
                    fullWidth disabled size="small"
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ── SECTION 3: Address Info ── */}
            <SectionCard>
              <Stack direction="row" alignItems="center" mb={2}>
                <InfoIcon sx={{ color: editMode ? 'primary.main' : 'text.secondary', opacity: editMode ? 1 : 0.6 }}>
                  <LocationOnIcon fontSize="small" />
                </InfoIcon>
                <Typography variant="subtitle1" fontWeight="bold" sx={{
                  ...(editMode && {
                    background: t => `linear-gradient(45deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
                    backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent',
                  }),
                  ...(!editMode && { color: 'text.secondary' }),
                }}>
                  Address Information
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <StyledTextField
                    label="Address" value={f('address')}
                    onChange={e => handleChange('address', e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <StyledTextField
                    label="Area" value={f('area')}
                    onChange={e => handleChange('area', e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  {/* Pincode with auto-fetch */}
                  <StyledTextField
                    label="Pincode" value={f('pincode')}
                    onChange={e => handlePincodeChange(e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                    inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                    InputProps={{
                      endAdornment: pincodeLoading
                        ? <CircularProgress size={16} sx={{ mr: 1 }} />
                        : null,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <StyledTextField
                    label="Village" value={f('village_name')}
                    onChange={e => handleChange('village_name', e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <StyledTextField
                    label="District" value={f('district')}
                    onChange={e => handleChange('district', e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <StyledTextField
                    label="State" value={f('state')}
                    onChange={e => handleChange('state', e.target.value)}
                    fullWidth disabled={!editMode} size="small"
                  />
                </Grid>
              </Grid>
            </SectionCard>
          </>
        )}
      </DialogContent>

      {!loading && member && (
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          {!editMode && !isSelf ? (
            <GradientButton
              startIcon={<EditIcon />}
              onClick={() => { setEditMode(true); setSuccess(false); }}
            >
              Edit Profile
            </GradientButton>
          ) : (
            <>
              <Button
                variant="outlined" onClick={handleCancel}
                sx={{ borderRadius: 3, textTransform: 'none' }}
                startIcon={<EditOffIcon />}
              >
                Cancel
              </Button>
              <GradientButton
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </GradientButton>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>

    {/* Success Modal */}
    <Dialog
      open={successOpen}
      onClose={() => {
        setSuccessOpen(false);
        if (onMemberUpdated) onMemberUpdated(member);
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          textAlign: 'center',
          minWidth: 320,
          p: 1,
        }
      }}
    >
      <DialogTitle sx={{
        background: t => `linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
        color: 'white', textAlign: 'center', fontWeight: 'bold', borderRadius: '12px 12px 0 0',
      }}>
        ✓ Success
      </DialogTitle>
      <DialogContent sx={{ pt: 3, pb: 1 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Profile Updated Successfully!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Changes have been saved.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <GradientButton
          sx={{ px: 5 }}
          onClick={() => {
            setSuccessOpen(false);
            if (onMemberUpdated) onMemberUpdated(member);
          }}
        >
          OK
        </GradientButton>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default MemberProfileDialog;
